import { useCallback, useEffect, useMemo, useState } from "react";

import {
    addMonths,
    endOfDay,
    isAfterDay,
    isBeforeDay,
    isSameDay,
    startOfDay,
} from "./dateUtils";
import { INTERVAL_PRESETS, INTERVAL_PRESET_MAP } from "../enums/presets";

export default function useIntervalPicker({
    controlRef,
    value,
    presets = INTERVAL_PRESETS,
    minDate,
    maxDate,
    onApply,
    onCancel,
}) {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    // Draft state — committed only on Apply
    const [draftFrom, setDraftFrom] = useState(value?.from ?? null);
    const [draftTo, setDraftTo] = useState(value?.to ?? null);
    const [presetId, setPresetId] = useState(value?.presetId ?? null);
    const [hoverDate, setHoverDate] = useState(null);
    const [pickStep, setPickStep] = useState("from"); // "from" | "to"

    const initialMonth = useMemo(() => {
        const base = value?.from ?? new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    }, [value?.from]);
    const [leftMonth, setLeftMonth] = useState(initialMonth);

    // Sync drafts when value changes externally (e.g. preset applied while closed)
    useEffect(() => {
        if (open) return;
        setDraftFrom(value?.from ?? null);
        setDraftTo(value?.to ?? null);
        setPresetId(value?.presetId ?? null);
        setPickStep("from");
        setHoverDate(null);
        const base = value?.from ?? new Date();
        setLeftMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    }, [value?.from, value?.to, value?.presetId, open]);

    const rightMonth = useMemo(() => addMonths(leftMonth, 1), [leftMonth]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setAnchorEl(null);
        setHoverDate(null);
    }, []);

    const handleCancel = useCallback(() => {
        handleClose();
        if (onCancel) onCancel();
    }, [handleClose, onCancel]);

    const handleApply = useCallback(() => {
        if (!draftFrom || !draftTo) return;
        const from = startOfDay(draftFrom);
        const to = endOfDay(draftTo);
        if (onApply) onApply({ presetId: presetId ?? "custom", from, to });
        handleClose();
    }, [draftFrom, draftTo, presetId, onApply, handleClose]);

    // Expose imperative API
    useEffect(() => {
        if (!controlRef) return;
        controlRef.current = {
            open(anchor, initial) {
                if (initial?.from) {
                    setDraftFrom(initial.from);
                    setDraftTo(initial.to ?? null);
                    setPresetId(initial.presetId ?? null);
                    const base = initial.from;
                    setLeftMonth(new Date(base.getFullYear(), base.getMonth(), 1));
                }
                setPickStep("to");
                setHoverDate(null);
                setAnchorEl(anchor || null);
                setOpen(true);
            },
            close() {
                handleClose();
            },
        };
    }, [controlRef, handleClose]);

    // Day click — toggle start/end
    const handleDayClick = useCallback((date) => {
        setPresetId(null);
        if (pickStep === "from" || !draftFrom || (draftFrom && draftTo)) {
            setDraftFrom(date);
            setDraftTo(null);
            setPickStep("to");
            return;
        }
        // pickStep === "to" and we have a from
        if (isBeforeDay(date, draftFrom)) {
            // User selected a date before "from" — make it the new from
            setDraftFrom(date);
            setDraftTo(null);
            setPickStep("to");
            return;
        }
        setDraftTo(date);
        setPickStep("from");
    }, [pickStep, draftFrom, draftTo]);

    const handleDayHover = useCallback((date) => {
        if (pickStep !== "to" || !draftFrom || draftTo) {
            setHoverDate(null);
            return;
        }
        setHoverDate(date);
    }, [pickStep, draftFrom, draftTo]);

    // Preset click — fill both
    const handlePresetClick = useCallback((id) => {
        const preset = INTERVAL_PRESET_MAP[id] || presets.find((p) => p.id === id);
        if (!preset) return;
        const range = preset.resolve();
        setDraftFrom(range.from);
        setDraftTo(range.to);
        setPresetId(id);
        setPickStep("from");
        setHoverDate(null);
        setLeftMonth(new Date(range.from.getFullYear(), range.from.getMonth(), 1));
    }, [presets]);

    // Header input edits
    const handleFromInput = useCallback((date) => {
        if (!date) return;
        setPresetId(null);
        setDraftFrom(date);
        if (draftTo && isAfterDay(date, draftTo)) {
            setDraftTo(null);
            setPickStep("to");
        }
        setLeftMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }, [draftTo]);

    const handleToInput = useCallback((date) => {
        if (!date) return;
        setPresetId(null);
        if (draftFrom && isBeforeDay(date, draftFrom)) return;
        setDraftTo(date);
        setPickStep("from");
    }, [draftFrom]);

    // Month nav
    const goPrevMonth = useCallback(() => {
        setLeftMonth((m) => addMonths(m, -1));
    }, []);
    const goNextMonth = useCallback(() => {
        setLeftMonth((m) => addMonths(m, 1));
    }, []);

    const setLeftMonthValue = useCallback((year, month) => {
        setLeftMonth(new Date(year, month, 1));
    }, []);

    const setRightMonthValue = useCallback((year, month) => {
        setLeftMonth(new Date(year, month - 1, 1));
    }, []);

    const canApply = !!draftFrom && !!draftTo;

    // Range helpers for the calendar
    const rangeEndForPreview = draftTo
        ?? (hoverDate && draftFrom && !isBeforeDay(hoverDate, draftFrom) ? hoverDate : null);

    function isInRange(d) {
        if (!draftFrom) return false;
        const end = rangeEndForPreview;
        if (!end) return false;
        const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const start = new Date(draftFrom.getFullYear(), draftFrom.getMonth(), draftFrom.getDate()).getTime();
        const finish = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
        return day >= start && day <= finish;
    }

    function isStart(d) {
        return isSameDay(d, draftFrom);
    }

    function isEnd(d) {
        if (draftTo) return isSameDay(d, draftTo);
        if (pickStep === "to" && hoverDate && draftFrom && !isBeforeDay(hoverDate, draftFrom)) {
            return isSameDay(d, hoverDate);
        }
        return false;
    }

    function isDisabled(d) {
        if (minDate && isBeforeDay(d, minDate)) return true;
        if (maxDate && isAfterDay(d, maxDate)) return true;
        return false;
    }

    return {
        open,
        anchorEl,
        leftMonth,
        rightMonth,
        draftFrom,
        draftTo,
        presetId,
        canApply,
        presets,
        minDate,
        maxDate,
        handleDayClick,
        handleDayHover,
        handlePresetClick,
        handleFromInput,
        handleToInput,
        goPrevMonth,
        goNextMonth,
        setLeftMonthValue,
        setRightMonthValue,
        handleApply,
        handleCancel,
        handleClose,
        isInRange,
        isStart,
        isEnd,
        isDisabled,
    };
}
